import { zodResolver } from "@hookform/resolvers/zod";
import { Megaphone, Pencil, Plus, Repeat, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AnnouncementBodyEditor } from "@/components/announcements/AnnouncementBodyEditor";
import { ANNOUNCEMENT_ICONS } from "@/components/announcements/announcement-icons";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { SearchInput } from "@/components/shared/search-input";
import { StatusPill, type StatusTone } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type AnnouncementAdminFilter,
  type AnnouncementListRow,
  useAnnouncementsAdmin,
  useCreateAnnouncement,
  useDeleteAnnouncement,
  useUpdateAnnouncement,
} from "@/hooks/use-announcements";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  ANNOUNCEMENT_ICON_LABELS,
  ANNOUNCEMENT_RECURRENCE_LABELS,
  announcementIconValues,
  announcementRecurrenceValues,
} from "@/schemas/announcements";

// ── datetime-local helpers ──

function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

// ── form schema (datetime-local strings; converted to ISO on submit) ──

const announcementFormSchema = z
  .object({
    title: z.string().min(1, "Give it a title").max(200),
    body: z.string().min(1, "Say something").max(5000),
    icon: z.enum(announcementIconValues),
    bgColor: z.string().regex(/^$|^#[0-9a-fA-F]{6}$/),
    linkLabel: z.string().trim().max(60),
    linkUrl: z.string().trim().max(500),
    startsAt: z.string().min(1, "Pick a start"),
    recurrence: z.enum(announcementRecurrenceValues),
  })
  .refine((d) => !d.linkUrl || /^https?:\/\/\S+$/i.test(d.linkUrl), {
    message: "Enter a full URL starting with http:// or https://",
    path: ["linkUrl"],
  })
  .refine((d) => Boolean(d.linkLabel) === Boolean(d.linkUrl), {
    message: "A button needs both a label and a URL",
    path: ["linkLabel"],
  });

type AnnouncementFormValues = z.infer<typeof announcementFormSchema>;

/** Card background presets — soft tints that keep the text readable. */
const BG_PRESETS = [
  { value: "", label: "None" },
  { value: "#fef9c3", label: "Yellow" },
  { value: "#dbeafe", label: "Blue" },
  { value: "#dcfce7", label: "Green" },
  { value: "#fee2e2", label: "Red" },
  { value: "#f3e8ff", label: "Purple" },
] as const;

// ── status pill ──

function statusFor(a: AnnouncementListRow): { label: string; tone: StatusTone } {
  // Announcements never expire — they're live from startsAt until each
  // employee dismisses their copy.
  return a.active ? { label: "Active", tone: "success" } : { label: "Scheduled", tone: "info" };
}

/** Plain-text preview of a possibly-HTML body for the table row. */
function bodyPreview(body: string): string {
  return body
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const ADMIN_TABS: { value: AnnouncementAdminFilter; label: string }[] = [
  { value: "current", label: "Current" },
  { value: "recurring", label: "Recurring" },
];

export function SettingsAnnouncementsPage() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [tab, setTab] = useState<AnnouncementAdminFilter>("current");
  const { data, isLoading, isError, error, refetch } = useAnnouncementsAdmin({
    search: debouncedSearch,
    filter: tab,
  });
  const remove = useDeleteAnnouncement();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AnnouncementListRow | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<AnnouncementListRow | null>(null);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load announcements"}
        onRetry={() => refetch()}
      />
    );
  }

  const rows = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex gap-1 rounded-lg bg-muted p-1 w-fit" role="tablist">
        {ADMIN_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            onClick={() => setTab(t.value)}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              tab === t.value
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <DataToolbar
        search={
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search announcements…"
            className="flex-1 min-w-[200px] max-w-sm"
          />
        }
        actions={
          <Button
            onClick={() => {
              setEditTarget(undefined);
              setDialogOpen(true);
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            New announcement
          </Button>
        }
      />

      <DataTable
        headers={[
          { label: "Announcement" },
          { label: "Schedule" },
          { label: "Status" },
          { label: "Created by", className: "hidden lg:table-cell" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Megaphone}
            title={tab === "current" ? "No current announcements" : "No recurring announcements"}
            description={
              tab === "current" ? "Create one and it appears on every employee's dashboard." : ""
            }
          />
        }
      >
        {rows.map((a) => {
          const status = statusFor(a);
          return (
            <tr
              key={a.id}
              className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors align-top"
            >
              <td className="px-4 py-3 text-sm max-w-md">
                <div className="font-medium">{a.title}</div>
                <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                  {bodyPreview(a.body)}
                </div>
              </td>
              <td className="px-4 py-3 text-sm whitespace-nowrap">
                <div>{formatWhen(a.startsAt)}</div>
                {a.recurrence !== "none" && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                    <Repeat className="h-3 w-3" />
                    {ANNOUNCEMENT_RECURRENCE_LABELS[a.recurrence]}
                  </div>
                )}
              </td>
              <td className="px-4 py-3">
                <StatusPill tone={status.tone} label={status.label} />
              </td>
              <td className="hidden lg:table-cell px-4 py-3 text-sm text-muted-foreground">
                {a.createdByName ?? "—"}
              </td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setEditTarget(a);
                    setDialogOpen(true);
                  }}
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteTarget(a)}
                  className="text-destructive hover:text-destructive"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          );
        })}
      </DataTable>

      {dialogOpen && (
        <AnnouncementDialog
          key={editTarget?.id ?? "new"}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          announcement={editTarget}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          open
          onOpenChange={() => setDeleteTarget(null)}
          title="Delete announcement?"
          description={`“${deleteTarget.title}” disappears from every dashboard, including everyone's prior announcements. The audit log keeps a record.`}
          confirmLabel="Delete"
          destructive
          onConfirm={() => {
            remove.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
          }}
        />
      )}
    </div>
  );
}

function AnnouncementDialog({
  open,
  onOpenChange,
  announcement,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  announcement?: AnnouncementListRow;
}) {
  const isEdit = announcement != null;
  const create = useCreateAnnouncement();
  const update = useUpdateAnnouncement();
  const form = useForm<AnnouncementFormValues>({
    resolver: zodResolver(announcementFormSchema),
    defaultValues: {
      title: announcement?.title ?? "",
      body: announcement?.body ?? "",
      icon: announcement?.icon ?? "megaphone",
      bgColor: announcement?.bgColor ?? "",
      linkLabel: announcement?.linkLabel ?? "",
      linkUrl: announcement?.linkUrl ?? "",
      // Default start is right now — the announcement shows immediately.
      startsAt: toLocalInputValue(announcement ? new Date(announcement.startsAt) : new Date()),
      recurrence: announcement?.recurrence ?? "none",
    },
  });

  async function onSubmit(values: AnnouncementFormValues) {
    const payload = {
      title: values.title,
      body: values.body,
      icon: values.icon,
      bgColor: values.bgColor || null,
      linkLabel: values.linkLabel || null,
      linkUrl: values.linkUrl || null,
      startsAt: new Date(values.startsAt).toISOString(),
      recurrence: values.recurrence,
    };
    if (isEdit) await update.mutateAsync({ id: announcement.id, ...payload });
    else await create.mutateAsync(payload);
    form.reset();
    onOpenChange(false);
  }

  const pending = create.isPending || update.isPending;
  const recurrence = form.watch("recurrence");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit announcement" : "New announcement"}</DialogTitle>
          <DialogDescription>
            Shows on every employee's dashboard while it's active.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Q3 All-Hands — Thursday, 10:00 ET" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <AnnouncementBodyEditor value={field.value} onChange={field.onChange} />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="icon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Icon</FormLabel>
                  <div className="flex gap-2">
                    {announcementIconValues.map((key) => {
                      const Icon = ANNOUNCEMENT_ICONS[key];
                      const selected = field.value === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => field.onChange(key)}
                          title={ANNOUNCEMENT_ICON_LABELS[key]}
                          aria-label={ANNOUNCEMENT_ICON_LABELS[key]}
                          aria-pressed={selected}
                          className={`flex size-9 items-center justify-center rounded-lg border transition-colors ${
                            selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : "text-muted-foreground hover:border-input hover:text-foreground"
                          }`}
                        >
                          <Icon className="size-4" />
                        </button>
                      );
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bgColor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Card background</FormLabel>
                  <div className="flex items-center gap-2">
                    {BG_PRESETS.map((preset) => {
                      const selected = field.value === preset.value;
                      return (
                        <button
                          key={preset.label}
                          type="button"
                          onClick={() => field.onChange(preset.value)}
                          title={preset.label}
                          aria-label={`${preset.label} background`}
                          aria-pressed={selected}
                          className={`size-8 rounded-lg border-2 transition-colors ${
                            selected ? "border-primary" : "border-input hover:border-ring"
                          }`}
                          style={preset.value ? { backgroundColor: preset.value } : undefined}
                        >
                          {!preset.value && (
                            <span className="block text-3xs font-semibold text-muted-foreground">
                              —
                            </span>
                          )}
                        </button>
                      );
                    })}
                    <label
                      className="ml-1 flex cursor-pointer items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
                      title="Custom color"
                    >
                      <input
                        type="color"
                        value={field.value || "#ffffff"}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="size-7 cursor-pointer rounded border border-input bg-transparent p-0.5"
                      />
                      Custom
                    </label>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="linkLabel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Button label (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Sign off time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="linkUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Button URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://…" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startsAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Starts</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="recurrence"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Repeats</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {announcementRecurrenceValues.map((r) => (
                          <SelectItem key={r} value={r}>
                            {ANNOUNCEMENT_RECURRENCE_LABELS[r]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {recurrence === "none"
                ? "Shows on every dashboard until each person dismisses it."
                : `Re-appears ${recurrence === "weekly" ? "every week" : "every month"} — even for people who dismissed the previous showing.`}
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? "Saving…" : isEdit ? "Save" : "Publish"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
